import React, { ReactElement, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import {
  Col, Radio, RadioChangeEvent, Row, Space, Tooltip,
} from 'antd';
import * as M from 'moment/moment';
import { extendMoment } from 'moment-range';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { setPmState } from '../../store/entities/pm';
import HeatmapExport from './components/HeatmapExport';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import Intersection from '../../types/Intersection';
import { IntersectionId } from '../../types/primitives';
// import utc from 'dayjs/plugin/utc';
// import tz from 'dayjs/plugin/timezone';
import { TimeZoneResponse } from '../../types/TimeZoneResponse';
import { getAggDataURL } from '../../util/urlApi';
import { AggDataResponseItem } from '../../types/Response';
import { TIMEZONE_API_KEY } from '../../config';
import { AggDataElement } from '../../types/AggDataElement';
import { ChartType } from '../../types/ChartType';
import { Direction } from '../../types/Direction';
import { Movement } from '../../types/Movement';
import HeatmapBase from './components/HeatmapBase';
import { convertToAggData, createExportData, getValue } from '../../util/util';
import { TITLE_MAP } from '../../util/constants';

const moment = extendMoment(M);
const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
const BASE_TIMEZONE_URL = 'https://maps.googleapis.com/maps/api/timezone/json';

const HeatmapPage = (): ReactElement => {
  const dispatch = useAppDispatch();
  const startDate = useAppSelector((state) => state.entities.pm.startDate);
  const endDate = useAppSelector((state) => state.entities.pm.endDate);
  const availableIntersections = useAppSelector((state) => (
    state.entities.pm.availableIntersections));
  const selectedIntersections = useAppSelector((state) => state.entities.pm.selectedIntersections);
  const sessionId = useAppSelector((state) => state.entities.pm.sessionId);
  const selectedDaysOfWeek = useAppSelector((state) => state.entities.pm.selectedDaysOfWeek);
  const submitted = useAppSelector((state) => state.entities.pm.submitted);

  type MovementButtonDisabledMap = {
    [movement in Movement]: boolean;
  };

  type ButtonDisabledMap = {
    [chart in ChartType]: MovementButtonDisabledMap;
  };

  const defaultButtonDisabledMap: ButtonDisabledMap = {
    aog: {
      Left: true,
      Through: true,
      Right: true,
    },
    sf: {
      Left: true,
      Through: true,
      Right: true,
    },
    dsb: {
      Left: true,
      Through: true,
      Right: true,
    },
    los: {
      Left: true,
      Through: true,
      Right: true,
    },
  };

  const [selectedChart, setSelectedChart] = useState<ChartType>('aog');
  const [selectedMovement, setSelectedMovement] = useState<Movement>('Through');
  const [aggData, setAggData] = useState<AggDataElement[]>([]);
  const [buttonDisabledMap, setButtonDisabledMap] = useState<ButtonDisabledMap>(
    defaultButtonDisabledMap,
  );
  // const [selectedFlow, setSelectedFlow] = useState<string>("through");

  useEffect(() => {
    if (submitted) {
      const getAggData = async (start: string, end: string,
        intersectionIds: IntersectionId[]) => {
        if (!availableIntersections || !selectedDaysOfWeek.length) return;
        const timezoneRequestResults: AxiosResponse[] = [];

        const timezoneMap: { [key: IntersectionId]: string } = {};

        // Make a timezone request for each intersection and keep the promises in an array
        const timezoneRequestPromises = intersectionIds.map((intersectionId) => {
          const timezoneUrl = new URL(BASE_TIMEZONE_URL);
          // Pick out the intersection using its Id
          const intersection: Intersection = availableIntersections
            .filter((int) => int.intersectionId === intersectionId)[0];
          timezoneUrl.searchParams.append('location',
            `${intersection.position.lat},${intersection.position.lng}`);
          timezoneUrl.searchParams.append('timestamp',
            String(moment.utc(start)
              .unix()));
          timezoneUrl.searchParams.append('key', TIMEZONE_API_KEY);

          // Make the request
          return axios.get<TimeZoneResponse>(timezoneUrl.toString())
            .then((res) => {
              // res.data.intersectionId = intersection.intersectionId;
              const resWithIntersection = {
                ...res,
                intersectionId: intersection.intersectionId,
              };
              timezoneMap[intersectionId] = res.data.timeZoneId;
              timezoneRequestResults.push(resWithIntersection);
            })
            .catch(() => {
              toast.error('Error getting timezones');
              dispatch(setPmState({ isDataLoading: false }));
            });
        });

        // Wait for all the requests to complete
        await Promise.all(timezoneRequestPromises);

        dispatch(setPmState({ timezoneMap }));

        // Make a moment-range for each with the timezones
        const timeRanges = timezoneRequestResults.map((res) => {
          const moments = [start, end].map((d) => moment.tz(d, res.data.timeZoneId)
            .utc());
          return moment.range(moments[0], moments[1]);
        });

        // Merge them into one.
        // This will go from the start of the earliest intersection to the end of the last.
        // For example, if some intersections are Eastern and others are Central time,
        // it will be 25 hours.
        const mergedRange = timeRanges[0];
        for (let i = 1; i < timeRanges.length; i++) {
          mergedRange.add(timeRanges[i]);
        }

        const url = getAggDataURL(mergedRange.start.format(dateTimeFormat),
          mergedRange.end.format(dateTimeFormat),
          intersectionIds,
          selectedDaysOfWeek,
          '00:00:00',
          '23:59:59');
        // Get the data from the backend
        const result = await axios.get(url, { headers: { Authorization: `Bearer ${sessionId}` } });
        // Parse it
        const rawAggData = Papa.parse<AggDataResponseItem>(result.data, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
        }).data;

        // Convert to AggDataResponseItems
        const convertedData = convertToAggData(rawAggData);

        setAggData(convertedData);

        const trajCountDict = convertedData
          .reduce<{ [key: IntersectionId]: number }>((acc, curr) => {
          if (curr.intersectionId in acc) {
            acc[curr.intersectionId] += curr.trajCnt;
          } else {
            acc[curr.intersectionId] = curr.trajCnt || 0;
          }
          return acc;
        }, {});

        dispatch(setPmState({ intersectionTrajCountDict: trajCountDict }));

        const newButtonDisabledMap: ButtonDisabledMap = ['aog', 'sf', 'dsb', 'los']
          .map((c) => c as ChartType)
          .reduce((acc: ButtonDisabledMap, curr) => {
            acc[curr] = {
              Left: isButtonDisabled(convertedData, curr, 'Left'),
              Through: isButtonDisabled(convertedData, curr, 'Through'),
              Right: isButtonDisabled(convertedData, curr, 'Right'),
            };
            return acc;
          }, defaultButtonDisabledMap);

        setButtonDisabledMap(newButtonDisabledMap);
      };

      getAggData(startDate, endDate, selectedIntersections)
        .then(() => {
          dispatch(setPmState({ isDataLoading: false }));
        })
        .catch((e) => {
          toast.error('Request failed.');
          // eslint-disable-next-line no-console
          console.error(e);
        });

      dispatch(setPmState({
        submitted: false,
        isDataLoading: true,
      }));
    }
  }, [submitted, selectedIntersections, sessionId]);

  const onMovementChange = (e: RadioChangeEvent) => setSelectedMovement(e.target.value);
  const onChartChange = (e: RadioChangeEvent) => setSelectedChart(e.target.value);

  const isButtonDisabled = (
    aggDataToUse: AggDataElement[], chartType: ChartType, movement: Movement,
  ): boolean => {
    const filtered = aggDataToUse
      .filter((a) => a.movement === movement)
      .map((a) => getValue(a, chartType));
    const applicableData = filtered.filter((a) => a !== null || a !== undefined);
    return !applicableData.length;
  };

  return (
    <>
      <div style={{
        width: '100%',
        height: '100%',
      }}
      >
        <Row style={{}}>
          <Space>
            <Space direction="vertical">
              <span className="input-label">Performance Measure</span>
              <Radio.Group onChange={onChartChange} value={selectedChart}>
                {['aog', 'sf', 'dsb', 'los']
                  .map((c) => c as ChartType)
                  .map((c) => (
                    <Tooltip title={TITLE_MAP[c]} key={`${c}-tooltip`}>
                      <Radio.Button
                        disabled={buttonDisabledMap[c][selectedMovement]}
                        value={c}
                        key={`${c}-button`}
                      >
                        {c.toUpperCase()}
                      </Radio.Button>
                    </Tooltip>

                  ))}
              </Radio.Group>
            </Space>
            <Space direction="vertical">
              <span className="input-label">Movement</span>
              <Radio.Group onChange={onMovementChange} value={selectedMovement}>
                {['Left', 'Through', 'Right']
                  .map((m) => m as Movement)
                  .map((m) => (
                    <Radio.Button
                      disabled={buttonDisabledMap[selectedChart][m]}
                      value={m}
                      key={m}
                    >
                      {m}
                    </Radio.Button>
                  ))}
              </Radio.Group>
            </Space>
            <HeatmapExport
              style={{ marginTop: '30px' }}
              startDate={startDate}
              endDate={endDate}
              exportData={createExportData(aggData.filter((d) => d.movement === selectedMovement))}
            />
          </Space>
          <Col style={{ width: '60%' }} />
        </Row>
        <Space
          direction="vertical"
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          {(['NB', 'SB', 'WB', 'EB'] as Direction[]).map((dir) => (
            <HeatmapBase
              key={dir}
              // id={`${dirIndex}-${selectedChart}`}
              style={{ width: '100%' }}
              type={selectedChart}
              direction={dir}
              selectedChart={selectedChart}
              selectedMovement={selectedMovement}
              data={aggData.filter((e) => e.direction === dir && e.movement === selectedMovement)}
            />
          ))}
        </Space>
      </div>
    </>
  );
};

export default HeatmapPage;
